import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Select } from './ui';
import { Trash2, X, CheckSquare } from 'lucide-react';

/**
 * BulkActionsToolbar Component
 * 
 * Floating toolbar for bulk operations on selected items.
 * Shows when items are selected with options to:
 * - Delete selected items
 * - Change status of selected items
 * - Clear selection
 */
const BulkActionsToolbar = React.memo(function BulkActionsToolbar({
  selectedCount,
  onDelete,
  onStatusChange,
  onClearSelection
}) {
  const [selectedStatus, setSelectedStatus] = useState('');

  const handleStatusChange = useCallback((e) => {
    const status = e.target.value;
    if (status) {
      onStatusChange?.(status);
      setSelectedStatus('');
    }
  }, [onStatusChange]);

  const handleDelete = useCallback(() => {
    if (confirm(`დარწმუნებული ხართ რომ გინდათ ${selectedCount} ნივთის წაშლა?`)) {
      onDelete?.();
    }
  }, [onDelete, selectedCount]);

  const statusOptions = [
    { value: 'ordered', label: 'გამოწერილია' },
    { value: 'at_carrier', label: 'გადამზიდავში' },
    { value: 'shipped', label: 'გამოგზავნილია' },
    { value: 'arrived', label: 'ჩამოტანილია' },
    { value: 'at_home', label: 'სახლში/საწყობში' },
    { value: 'delivered_to_client', label: 'გაცემული' }
  ];

  if (selectedCount === 0) return null;

  return (
    <div className="fixed bottom-20 md:bottom-6 left-1/2 transform -translate-x-1/2 z-40 animate-slide-up">
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-pink-200 dark:border-pink-800 px-4 py-3 flex items-center gap-3 min-w-[320px]">
        {/* Selected count */}
        <div className="flex items-center gap-2 px-3 py-1.5 bg-pink-50 dark:bg-pink-900/30 rounded-xl">
          <CheckSquare className="h-4 w-4 text-pink-600" />
          <span className="text-sm font-semibold text-pink-700 dark:text-pink-300">
            არჩეულია: {selectedCount}
          </span>
        </div>

        {/* Divider */}
        <div className="w-px h-8 bg-gray-200 dark:bg-gray-700" />

        {/* Status change dropdown */}
        <div className="flex items-center gap-2">
          <Select
            value={selectedStatus}
            onChange={handleStatusChange}
            className="h-10 min-w-[140px] rounded-xl border-gray-200 text-sm"
          >
            <option value="">სტატუსის შეცვლა</option>
            {statusOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        {/* Delete button */}
        <Button
          variant="destructive"
          size="sm"
          onClick={handleDelete}
          className="h-10 rounded-xl flex items-center gap-1.5"
        >
          <Trash2 className="h-4 w-4" />
          <span className="hidden sm:inline">წაშლა</span>
        </Button>

        {/* Clear button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearSelection}
          className="h-10 w-10 p-0 rounded-xl"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
});

BulkActionsToolbar.propTypes = {
  selectedCount: PropTypes.number.isRequired,
  onDelete: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired,
  onClearSelection: PropTypes.func.isRequired
};

export default BulkActionsToolbar;